// 4 summary tiles above the campaigns list. Values come from useCampaignStats;
// deltas below each value reflect month-over-month change. Matches the
// dashboard design in docs/crm-campaings/design-prototype/preview-dashboard.png.

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
// Tiles use only styled-components; no UI primitives needed.
import { useCampaignStats } from '../../hooks/useCampaignsState';
const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  flex: 1 1 200px;
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const Value = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.NEU_PRIMARY};
  line-height: 1.2;
`;

const Delta = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $positive, theme }) => ($positive ? theme.colors.SUCCESS_GREEN : theme.colors.ERROR_RED)};
`;

const Skeleton = styled.div`
  flex: 1 1 200px;
  height: 84px;
  background: ${({ theme }) => theme.colors.NEU_50};
  border-radius: 12px;
`;

const formatDelta = (delta, { suffix = '', prefix = '+' } = {}) => {
  if (delta == null) return '';
  const positive = delta >= 0;
  const sign = positive ? prefix : '';
  return `${sign}${delta}${suffix}`;
};

const CampaignStatsGrid = ({ dateRange }) => {
  const { t } = useTranslation();
  const { stats, isLoading } = useCampaignStats(dateRange);

  if (isLoading) {
    return (
      <Grid>
        <Skeleton /><Skeleton /><Skeleton /><Skeleton />
      </Grid>
    );
  }

  return (
    <Grid>
      <Card>
        <Label>{t('campaigns.stats.active', 'Active')}</Label>
        <Value>{stats.activeCampaigns}</Value>
        <Delta $positive={stats.activeDelta >= 0}>
          {formatDelta(stats.activeDelta, { suffix: ' vs last month' })}
        </Delta>
      </Card>
      <Card>
        <Label>{t('campaigns.stats.sent', 'Sent')}</Label>
        <Value>{stats.totalSent.toLocaleString()}</Value>
        <Delta $positive={stats.sentDelta >= 0}>
          {formatDelta(stats.sentDelta, { suffix: '%' })}
        </Delta>
      </Card>
      <Card>
        <Label>{t('campaigns.stats.openRate', 'Avg. open rate')}</Label>
        <Value>{stats.averageOpenRate?.toFixed(1)}%</Value>
        <Delta $positive={stats.openRateDelta >= 0}>
          {formatDelta(stats.openRateDelta, { suffix: ' pts' })}
        </Delta>
      </Card>
      <Card>
        <Label>{t('campaigns.stats.revenue', 'Attributed revenue')}</Label>
        <Value>${stats.totalRevenue.toLocaleString()}</Value>
        <Delta $positive={stats.revenueDelta >= 0}>
          {formatDelta(stats.revenueDelta, { prefix: '+$', suffix: '' })}
        </Delta>
      </Card>
    </Grid>
  );
};

export default CampaignStatsGrid;
